import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  getDoc,
  updateDoc,
} from 'firebase/firestore';
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { db } from '../firebase';
import { Group } from '../models/group';
import { Schedule } from '../models/schedule';

export const useGroup = (groupId: string) => {
  const groupRef = useMemo(() => doc(db, 'groups', groupId), [groupId]);
  const queryKey = `/group/${groupId}`;
  const queryClient = useQueryClient();

  const { data: group } = useQuery(queryKey, async () => {
    const snapshot = await getDoc(groupRef);
    if (snapshot.exists()) {
      return { id: snapshot.id, ...snapshot.data() } as Group;
    }
    return null;
  });

  const { mutate: participate } = useMutation(
    async (userId: string) => {
      updateDoc(groupRef, {
        members: arrayUnion(userId),
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
      },
    }
  );

  const { mutate: cancel } = useMutation(
    async (userId: string) => {
      updateDoc(groupRef, {
        members: arrayRemove(userId),
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
      },
    }
  );

  const { mutate: addBoardGame } = useMutation(
    async (boardGameId: string) => {
      updateDoc(groupRef, {
        boardGames: arrayUnion(boardGameId),
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
      },
    }
  );

  const { mutate: addSchedule } = useMutation(
    async (schedule: Omit<Schedule, 'id'>) => {
      addDoc(collection(groupRef, 'schedules'), schedule);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
      },
    }
  );

  return {
    group,
    participate,
    cancel,
    addBoardGame,
    addSchedule,
  };
};
